import { AddCircle, Person } from "@mui/icons-material";
import { Autocomplete, Box, Chip, Grid, Stack, TextField } from "@mui/material";
import React, { Fragment } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import usePetViewRoute from "../../features/pets/hooks/usePetViewRoute";
import { CHIP_MIN_WIDTH } from "../../enums/Styles";
import NewUserForm from "../modals/bookingModalComponents/components/NewUserForm";

export default function UserSelector({
  users,
  name = "user",
  label = "SelectUser",
  handlePets,
  isNewUserSelected = false,
  toggleNewUserSelected,
  showNewUserForm = true,
  testId,
}) {
  const [newUser, setNewUser] = React.useState(isNewUserSelected);
  const { setValue } = useFormContext();
  const { t } = useTranslation();

  function toggleView() {
    handleNewUser();
    setNewUser((prev) => !prev);
    toggleNewUserSelected();
    setValue("user", null);
  }

  function handleNewUser() {
    setValue("pet", null);
  }

  if (!showNewUserForm) {
    return (
      <ExistingUser
        users={users}
        name={name}
        label={label}
        handlePets={handlePets}
        handleNewUser={() => handleNewUser()}
        testId={testId}
      />
    );
  }

  return (
    <Fragment>
      <Stack direction={"row"} spacing={1} mb={3}>
        <Chip
          clickable
          sx={{
            minWidth: CHIP_MIN_WIDTH,
            display: "flex",
            justifyContent: "start",
          }}
          onClick={() => toggleView()}
          icon={<Person />}
          label={t("ExistingUser")}
          color={!newUser ? "primary" : "default"}
        />
        <Chip
          clickable
          sx={{
            minWidth: CHIP_MIN_WIDTH,
            display: "flex",
            justifyContent: "start",
          }}
          onClick={() => toggleView()}
          icon={<AddCircle />}
          label={t("NewUser")}
          color={newUser ? "primary" : "default"}
        />
      </Stack>
      {newUser ? (
        <Grid container spacing={2}>
          <NewUserForm />
        </Grid>
      ) : (
        <ExistingUser
          users={users}
          name={name}
          label={label}
          handleNewUser={() => handleNewUser()}
          handlePets={handlePets}
          testId={testId}
        />
      )}
    </Fragment>
  );
}

function ExistingUser({
  users,
  name = "user",
  label = "SelectUser",
  handlePets,
  handleNewUser,
  testId,
}) {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const params = useParams();
  const { ownerId: petViewOwnerId } = usePetViewRoute();
  const defaultUser = getDefaultUser();

  function getDefaultUser() {
    const userRegex = /^clinic\/user\/(\d+)$/;
    const userId = String(params["*"]).match(userRegex);

    if (userId) {
      return userId[1];
    } else if (petViewOwnerId) {
      return petViewOwnerId;
    } else {
      return null;
    }
  }

  React.useEffect(() => {
    if (defaultUser) {
      setValue(
        name,
        users.find((user) => Number(user?.id) === Number(defaultUser)),
      );
    }
  }, [defaultUser, setValue]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Autocomplete
          fullWidth
          {...field}
          value={
            field.value ??
            users.find((user) => Number(user?.id) === Number(defaultUser)) ??
            null
          }
          defaultValue={
            users.find((user) => Number(user?.id) === Number(defaultUser)) ??
            null
          }
          onChange={(e, value) => {
            field.onChange(value);
            handleNewUser();
            if (handlePets) {
              handlePets(value);
            }
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          options={users}
          getOptionLabel={(option) =>
            `${option.userDetails.firstName} ${option.userDetails.lastName ? option.userDetails.lastName : ""} ${option.userDetails.email ? option.userDetails.email : ""}`
          }
          renderOption={(props, option) => (
            <Box {...props} key={option.id}>
              {option.userDetails.firstName} {option.userDetails.lastName ? option.userDetails.lastName : ""}{" "}
              {option.userDetails.email}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t(label) ?? t("Users")}
              inputProps={{
                ...params.inputProps,
                ...(testId && { "data-testid": testId }),
              }}
            />
          )}
          clearText={t("Clear")}
          closeText={t("Close")}
          openText={t("Open")}
        />
      )}
    />
  );
}

function isStringEmpty(string) {
  return String(string).length > 0 ? string : "";
}